import React, { useState } from 'react';
import { Phone, Mail, Globe, ArrowRight, CheckCircle2, ShieldCheck, Loader2, Send } from 'lucide-react';
import { companyInfo } from '../../data/companyInfo';
import { servicesData } from '../../data/servicesData';
import { sendContactInquiry } from '../../services/emailService';

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: servicesData[0].title,
    budget: '$5,000 - $15,000',
    message: '',
    consent: true,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.message.trim()) {
      setErrorMessage('Please fill in your name, email, and project message.');
      return;
    }
    setErrorMessage(null);
    setLoading(true);

    try {
      const res = await sendContactInquiry({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        budget: formData.budget,
        message: formData.message,
      });

      setLoading(false);
      if (res.success) {
        setSubmitted(true);
      } else {
        setErrorMessage(res.error || 'Failed to submit inquiry. Please try again or email us directly at info@ebonmedia.com');
      }
    } catch (err: any) {
      setLoading(false);
      setErrorMessage('Submission error. Please email us directly at info@ebonmedia.com');
    }
  };

  return (
    <section id="contact-section" className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      {/* Background glow accents */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#159DE8]/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF851B]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Contact Info & Value Proposition */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] block mb-2">
                GET IN TOUCH
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display">
                Let's Build Something Better
              </h2>
            </div>

            <p className="text-sm sm:text-base text-[#D5DFEC] leading-relaxed">
              Tell us about your project, and our team will get back to you with the right solution, realistic architectural blueprint, and clear timelines.
            </p>

            <div className="space-y-4 pt-4">
              <a
                href={`tel:${companyInfo.contact.phone}`}
                className="flex items-center gap-3.5 p-3.5 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#FF851B] transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#FF851B]/10 text-[#FF851B] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Phone size={18} />
                </div>
                <div>
                  <span className="block text-[11px] text-[#8FA0B8]">Phone / WhatsApp:</span>
                  <span className="text-sm font-bold text-white group-hover:text-[#FF851B] transition-colors">
                    {companyInfo.contact.phoneDisplay}
                  </span>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.contact.email}`}
                className="flex items-center gap-3.5 p-3.5 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8] transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#159DE8]/10 text-[#159DE8] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Mail size={18} />
                </div>
                <div>
                  <span className="block text-[11px] text-[#8FA0B8]">Direct Email:</span>
                  <span className="text-sm font-bold text-white group-hover:text-[#159DE8] transition-colors">
                    {companyInfo.contact.email}
                  </span>
                </div>
              </a>
            </div>

            <div className="p-4 bg-[#071426] border border-[#1b2f4a] rounded-xl text-xs text-[#8FA0B8] flex items-start gap-2.5">
              <ShieldCheck size={18} className="text-[#159DE8] shrink-0 mt-0.5" />
              <span>We uphold rigorous data protection standards. Your intellectual property and concept specifics remain strictly confidential under mutual NDA.</span>
            </div>
          </div>

          {/* Right Column: High-Conversion Form */}
          <div className="lg:col-span-7">
            <div className="p-6 sm:p-8 rounded-2xl bg-[#071426] border border-[#1b2f4a] shadow-2xl relative">
              
              {submitted ? (
                <div className="py-12 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-green-500/20 text-[#00C853] rounded-full flex items-center justify-center border border-green-500/40">
                    <CheckCircle2 size={36} />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-display">Inquiry Successfully Received!</h3>
                  <p className="text-sm text-[#D5DFEC] max-w-md mx-auto leading-relaxed">
                    Thank you, <strong className="text-white">{formData.fullName}</strong>. Our technology team has registered your requirements. We will review your brief and respond directly via email at <strong className="text-[#159DE8]">{formData.email}</strong> within 24 business hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        company: '',
                        service: servicesData[0].title,
                        budget: '$5,000 - $15,000',
                        message: '',
                        consent: true,
                      });
                    }}
                    className="mt-4 px-6 py-2.5 rounded-full bg-[#0B2342] text-xs font-semibold text-white hover:bg-[#10223b] border border-[#1b2f4a] transition-colors"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="border-b border-[#1b2f4a] pb-3 mb-2 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white font-display">
                      Project Inquiry
                    </h3>
                    <span className="text-[11px] text-[#8FA0B8]">* Required fields</span>
                  </div>

                  {errorMessage && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                      {errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="John Smith"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Business Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@company.com"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 00000 00000"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Service Required *</label>
                      <select
                        value={formData.service}
                        onChange={e => setFormData({ ...formData, service: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      >
                        {servicesData.map(s => (
                          <option key={s.id} value={s.title}>{s.title}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Company / Organization (Optional)</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Company name"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Project Budget (Optional)</label>
                      <select
                        value={formData.budget}
                        onChange={e => setFormData({ ...formData, budget: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                      >
                        <option value="Under $5,000">Under $5,000</option>
                        <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                        <option value="$15,000 - $40,000">$15,000 - $40,000</option>
                        <option value="$40,000+">$40,000+ (Enterprise)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Your Message / Project Details *</label>
                    <textarea
                      required
                      rows={3}
                      value={formData.message}
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Outline your requirements, current challenges, or goals..."
                      className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1 text-xs text-[#8FA0B8]">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={formData.consent}
                      onChange={e => setFormData({ ...formData, consent: e.target.checked })}
                      className="rounded border-[#1b2f4a] text-[#FF851B] focus:ring-[#FF851B]"
                    />
                    <label htmlFor="consent" className="cursor-pointer">
                      I agree to receive project correspondence from EBON Media Solutions.
                    </label>
                  </div>

                  <div className="pt-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-sm hover:shadow-glow-orange hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Submitting Inquiry...</span>
                        </>
                      ) : (
                        <>
                          <span>Request a Consultation</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
